import { SubscriptionPlan, SubscriptionPlanType, WorkspaceSeatType } from 'models/user'

import { workspacePlanType } from './workspaceMemberLimit'

export interface MemberAccessItem {
  seatType: WorkspaceSeatType
  title: string
  subTitle: string
}

export enum RestrictedAccessOption {
  FULL_ACCESS = 'full_access',
  RESTRICTED_ACCESS = 'restricted_access',
  SPONSORED_ACCESS = 'sponsored_access',
}

/**
 * Seat/member types selectable for a workspace, per subscription plan (ISSUE-2078). Drives both the
 * invite-members form and the update-member seat-type dropdown so they never offer a type the plan
 * disallows.
 *
 * - Personal/Starter: always Restricted only (free seats are Restricted on the backend).
 * - Enterprise: Full + Sponsored + Restricted.
 * - Pro (and any other paid plan): Full + Restricted.
 */
export const getAllowedSeatTypes = (planType: SubscriptionPlanType | undefined): WorkspaceSeatType[] => {
  if (planType === SubscriptionPlanType.PERSONAL) return [WorkspaceSeatType.RESTRICTED]

  if (planType === SubscriptionPlanType.ENTERPRISE) {
    return [WorkspaceSeatType.FULL, WorkspaceSeatType.SPONSORED, WorkspaceSeatType.RESTRICTED]
  }

  return [WorkspaceSeatType.FULL, WorkspaceSeatType.RESTRICTED]
}

/**
 * Resolve a workspace subscription plan to its plan-type bucket (ISSUE-2078). Falls back to PERSONAL
 * when the plan is missing but the workspace is known to be personal. Returns `undefined` only while the plan
 * is genuinely unknown (query pending/failed) — callers must then avoid sending an explicit seat type.
 */
export const getWorkspacePlanType = (plan: SubscriptionPlan | undefined, isWorkspacePersonal = false): SubscriptionPlanType | undefined =>
  plan ? workspacePlanType[plan] : isWorkspacePersonal ? SubscriptionPlanType.PERSONAL : undefined

/**
 * Translation key for a member's seat-type access label (ISSUE-2078). Single source of truth for the
 * FULL / RESTRICTED / SPONSORED copy so the label never drifts between the badges, dropdown titles and
 * confirmation popups that render it.
 */
export const getSeatTypeAccessLabelKey = (seatType: WorkspaceSeatType | undefined): RestrictedAccessOption => {
  switch (seatType) {
    case WorkspaceSeatType.SPONSORED:
      return RestrictedAccessOption.SPONSORED_ACCESS
    case WorkspaceSeatType.RESTRICTED:
      return RestrictedAccessOption.RESTRICTED_ACCESS
    default:
      return RestrictedAccessOption.FULL_ACCESS
  }
}

/**
 * Seat types selectable from the member access dropdown (ISSUE-2078). Three-way FULL / RESTRICTED
 * / SPONSORED — consumers must filter this by {@link getAllowedSeatTypes} so only the seat types the
 * plan permits are shown.
 */
export const accessDropdownItems: MemberAccessItem[] = [
  {
    seatType: WorkspaceSeatType.FULL,
    title: RestrictedAccessOption.FULL_ACCESS,
    subTitle: 'full_access_description',
  },
  {
    seatType: WorkspaceSeatType.SPONSORED,
    title: RestrictedAccessOption.SPONSORED_ACCESS,
    subTitle: 'sponsored_access_description',
  },
  {
    seatType: WorkspaceSeatType.RESTRICTED,
    title: RestrictedAccessOption.RESTRICTED_ACCESS,
    subTitle: 'restricted_access_description',
  },
]
